import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AssessmentProvider, useAssessmentContext, useAssessmentTooltips } from './AssessmentContext';
import TestDashboard from './dashboard/TestDashboard';
import TemplateBuilder from './Templates/TemplateBuilder';
import QuestionBankManager from './QuestionBank/QuestionBankManager';
import HRUploadManager from './HRUpload/HRUploadManager';
import MemberAssessmentDashboard from './student/MemberAssessmentDashboard';
import Layout from '../../components/Layout/Layout';
import { useUser } from '../../contexts/UserContext';
import ResultsEvaluation from './evaluation/ResultsEvaluation';
import AnalyticsDashboard from './dashboard/AnalyticsDashboard';
import {
  isAssessmentAdmin,
  isAssessmentBlocked,
  isAssessmentStudent,
} from '../../utils/assessmentStudent';
import api from '../../utils/api';

// Global redirection helper for student users within the assessment module
const checkAndRedirectStudent = (url) => {
  const token = localStorage.getItem('token');
  if (!token) {
    localStorage.removeItem('temp_student_profile');
    return false;
  }
  const tempStudent = localStorage.getItem('temp_student_profile');
  if (tempStudent) {
    let targetPath = '/';
    if (url) {
      try {
        const u = new URL(url, window.location.origin);
        targetPath = u.pathname;
      } catch {
        targetPath = String(url);
      }
    } else {
      targetPath = window.location.pathname;
    }

    const lowPath = targetPath.toLowerCase();
    const isAllowed =
      lowPath === '/' ||
      lowPath === '' ||
      lowPath.startsWith('/forgot-password') ||
      lowPath.startsWith('/assessment') ||
      lowPath.startsWith('/execution/') ||
      lowPath.startsWith('/workflow/');

    if (!isAllowed) {
      window.location.replace('/assessment');
      return true;
    }
  }
  return false;
};

// Check on initial load
checkAndRedirectStudent();

// Intercept dynamic routing / history changes
const originalPushState = window.history.pushState;
const originalReplaceState = window.history.replaceState;

window.history.pushState = function(state, unused, url) {
  if (checkAndRedirectStudent(url)) return;
  return originalPushState.apply(this, arguments);
};

window.history.replaceState = function(state, unused, url) {
  if (checkAndRedirectStudent(url)) return;
  return originalReplaceState.apply(this, arguments);
};

// Interceptor to handle student login/profile redirections within the assessment module
api.interceptors.response.use(
  (response) => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('temp_student_profile');
    }

    if (response.config?.url?.includes('/auth/login')) {
      const userData = response.data?.user;
      if (userData && (userData.is_student || Number(userData.role_id) === 15)) {
        localStorage.setItem('temp_student_profile', JSON.stringify({
          ...userData,
          role_id: 15,
          role: 15,
          is_student: true
        }));
      } else {
        localStorage.removeItem('temp_student_profile');
      }
    }

    if (response.config?.url?.includes('/auth/profile')) {
      const tempStudent = localStorage.getItem('temp_student_profile');
      if (tempStudent && token) {
        try {
          const parsed = JSON.parse(tempStudent);
          response.data = {
            user_id: parsed.user_id || parsed.id,
            first_name: parsed.first_name || parsed.name || '',
            last_name: parsed.last_name || '',
            email: parsed.email || '',
            phone: parsed.phone_no || parsed.phone || '',
            username: parsed.username || '',
            role_id: 15,
            role: 15,
            designation_id: 0,
            designation: 'Student',
            image: parsed.user_img || 'uploads/no_image.png',
            last_login: '',
            child_id: parsed.child_id || 0,
            cust_key: parsed.cust_key || 0,
            college_name: parsed.college_name || '',
            is_student: true,
            password: '',
          };
        } catch (e) {
          console.error("Error parsing temp student profile", e);
        }
      }
    }
    return response;
  },
  (error) => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('temp_student_profile');
    }

    if (error.config?.url?.includes('/auth/profile') && error.response?.status === 404) {
      const tempStudent = localStorage.getItem('temp_student_profile');
      if (tempStudent && token) {
        try {
          const parsed = JSON.parse(tempStudent);
          return {
            ...error,
            status: 200,
            data: {
              user_id: parsed.user_id || parsed.id,
              first_name: parsed.first_name || parsed.name || '',
              last_name: parsed.last_name || '',
              email: parsed.email || '',
              phone: parsed.phone_no || parsed.phone || '',
              username: parsed.username || '',
              role_id: 15,
              role: 15,
              designation_id: 0,
              designation: 'Student',
              image: parsed.user_img || 'uploads/no_image.png',
              last_login: '',
              child_id: parsed.child_id || 0,
              cust_key: parsed.cust_key || 0,
              college_name: parsed.college_name || '',
              is_student: true,
              password: '',
            }
          };
        } catch (e) {
          console.error("Error parsing temp student profile in error handler", e);
        }
      }
    }
    return Promise.reject(error);
  }
);

const FULL_HEIGHT_MODULES = new Set(['templates', 'questionBank', 'hrUpload']);

const AssessmentContent = () => {
  const navigate = useNavigate();
  useAssessmentTooltips();
  const { activeModule, moduleReturnTo, returnFromModule, selectedTestId } = useAssessmentContext();
  const useFullHeightLayout = FULL_HEIGHT_MODULES.has(activeModule);

  const handleHrUploadBack = () => {
    const returnPath = localStorage.getItem('hr_upload_return_path');
    if (returnPath) {
      localStorage.removeItem('hr_upload_return_path');
      navigate(returnPath);
    } else {
      returnFromModule();
    }
  };

  return (
    <div
      className={`bg-[#fcfcfc] flex flex-col max-lg:h-auto max-lg:overflow-visible ${
        useFullHeightLayout
          ? 'h-full min-h-0 overflow-hidden'
          : 'min-h-0 overflow-visible'
      }`}
    >
      <div
        className={`w-full max-lg:flex-initial max-lg:h-auto max-lg:overflow-visible ${
          useFullHeightLayout ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-0 overflow-visible'
        }`}
      >
        {activeModule === 'inventory' && <TestDashboard />}
        {activeModule === 'questionBank' && (
          <QuestionBankManager onBack={returnFromModule} returnToTemplates={moduleReturnTo === 'templates'} />
        )}
        {activeModule === 'templates' && (
          <TemplateBuilder onBack={returnFromModule} />
        )}
        {activeModule === 'evaluation' && (
          <ResultsEvaluation
            assessmentId={selectedTestId}
            onBack={returnFromModule}
          />
        )}
        {activeModule === 'hrUpload' && (
          <HRUploadManager onBack={handleHrUploadBack} />
        )}
        {activeModule === 'analytics' && (
          <AnalyticsDashboard onBack={returnFromModule} />
        )}
      </div>
    </div>
  );
};

const AssessmentPage = () => {
  const { user, loading } = useUser();
  const [viewAsStudent, setViewAsStudent] = React.useState(() => {
    return localStorage.getItem('assessment_view_as_student') === 'true';
  });

  React.useEffect(() => {
    const handleStorageChange = () => {
      setViewAsStudent(localStorage.getItem('assessment_view_as_student') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('assessment_view_mode_change', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('assessment_view_mode_change', handleStorageChange);
    };
  }, []);

  React.useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const handleNavigationIntercept = (url) => {
      if (url) {
        const urlStr = String(url);
        if ((urlStr.includes('/home?tab=employees') || urlStr.includes('/home')) && 
            sessionStorage.getItem('assessment_nav_from_result_analysis') === 'true') {
          sessionStorage.removeItem('assessment_nav_from_result_analysis');
          window.location.href = '/assessment';
          return true;
        } else if (!urlStr.includes('/employee-details')) {
          sessionStorage.removeItem('assessment_nav_from_result_analysis');
        }
      }
      return false;
    };

    window.history.pushState = function(state, unused, url) {
      if (handleNavigationIntercept(url)) return;
      return originalPushState.apply(this, arguments);
    };

    window.history.replaceState = function(state, unused, url) {
      if (handleNavigationIntercept(url)) return;
      return originalReplaceState.apply(this, arguments);
    };

    return () => {
      // Keep globally active so that it runs when user navigates away to /employee-details and clicks Back.
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (isAssessmentBlocked(user)) {
    return <Navigate to="/tickets" replace />;
  }

  const showAdminInventory = isAssessmentAdmin(user) && !viewAsStudent;

  return (
    <AssessmentProvider>
      <Layout noSidebar={true}>
        {showAdminInventory ? (
          <AssessmentContent />
        ) : (
          <MemberAssessmentDashboard />
        )}
      </Layout>
    </AssessmentProvider>
  );
};

export default AssessmentPage;
